"use client";

import {
  Box,
  HStack,
  IconButton,
  Input,
  Stack,
  mergeRefs,
  useControllableState,
} from "@chakra-ui/react";
import { forwardRef, useRef } from "react";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { InputGroup } from "./input-group";
import { useThemeConfig } from "../../hooks/useThemeConfig";

export const PasswordInput = forwardRef(
  /**
   * @typedef {object} PasswordInputProps
   * @prop {any} rootProps
   * @prop {boolean} defaultVisible
   * @prop {any} visible
   * @prop {() => void} onVisibleChange
   * @prop {{ on: import("react").ReactNode, off: import("react").ReactNode }} visibilityIcon
   */
  /**
   * @param {PasswordInputProps & import("@chakra-ui/react").InputProps} props
   */
  function PasswordInput(props, ref) {
    const {
      rootProps,
      defaultVisible,
      visible: visibleProp,
      onVisibleChange,
      visibilityIcon = { on: <LuEye />, off: <LuEyeOff /> },
      ...rest
    } = props;

    const [visible, setVisible] = useControllableState({
      value: visibleProp,
      defaultValue: defaultVisible || false,
      onChange: onVisibleChange,
    });

    const inputRef = useRef(null);

    const { inputProps } = useThemeConfig();

    return (
      <InputGroup
        width="full"
        endElement={
          // @ts-ignore
          <VisibilityTrigger
            disabled={rest.disabled}
            borderRadius="full"
            onPointerDown={
              // @ts-ignore
              (e) => {
                if (rest.disabled) {
                  return;
                }
                if (e.button !== 0) {
                  return;
                }
                e.preventDefault();
                setVisible(!visible);
              }
            }
          >
            {visible ? visibilityIcon.off : visibilityIcon.on}
          </VisibilityTrigger>
        }
        {...rootProps}
      >
        <Input
          {...inputProps}
          {...rest}
          ref={mergeRefs(ref, inputRef)}
          type={visible ? "text" : "password"}
        />
      </InputGroup>
    );
  },
);

const VisibilityTrigger = forwardRef(function VisibilityTrigger(props, ref) {
  return (
    <IconButton
      tabIndex={-1}
      ref={ref}
      me="-2"
      aspectRatio="square"
      size="sm"
      variant="ghost"
      height="calc(100% - {spacing.2})"
      aria-label="Toggle password visibility"
      {...props}
    />
  );
});

export const PasswordStrengthMeter = forwardRef(
  /**
   * @typedef {object} PasswordStrengthMeterProps
   * @prop {number} max
   * @prop {number} value
   */
  /**
   * @param {PasswordStrengthMeterProps & import("@chakra-ui/react").StackProps} props
   */
  function PasswordStrengthMeter(props, ref) {
    const { max = 4, value, ...rest } = props;

    const percent = (value / max) * 100;
    const { label, colorPalette } = getColorPalette(percent);

    return (
      <Stack align="flex-end" gap="1" ref={ref} {...rest}>
        <HStack width="full" ref={ref} {...rest}>
          {Array.from({ length: max }).map((_, index) => (
            <Box
              key={index}
              height="1"
              flex="1"
              rounded="sm"
              data-selected={index < value ? "" : undefined}
              layerStyle="fill.subtle"
              colorPalette="gray"
              _selected={{
                colorPalette,
                layerStyle: "fill.solid",
              }}
            />
          ))}
        </HStack>

        {label && <HStack textStyle="xs">{label}</HStack>}
      </Stack>
    );
  },
);

/**
 * @param {number} percent
 */
function getColorPalette(percent) {
  switch (true) {
    case percent < 33:
      return { label: "Low", colorPalette: "red" };
    case percent < 66:
      return { label: "Medium", colorPalette: "orange" };
    default:
      return { label: "High", colorPalette: "green" };
  }
}
